/**
 * Beta contract editor — the document-IR lane (ADR-0099).
 *
 * Same steps as the legacy editor (edit → granska → sign) and the same
 * shared pieces (StatusBar, ReviewPanel, SigningPanel, PartiesPanel,
 * property panels, BilagorTab), but the document is built from blocks:
 *
 *   - The form is derived from the composition (`describeCompositionForm`)
 *     instead of the template's variable schema.
 *   - The preview is the server-compiled IR (`contracts.ir_preview`)
 *     rendered as HTML in a sandboxed frame, with compiler diagnostics.
 *   - Fields bound to a party/property are filled automatically and can be
 *     switched to manual text per field (`contracts.set_autofill_mode`).
 *
 * Values, parties and properties persist through the same procedures as
 * the legacy lane; legacy contracts that land here are sent back to /edit.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Copy, Eye, FileText, PenLine } from 'lucide-react';
import {
  computeFormState,
  createRuleContext,
  describeCompositionForm,
  evaluateCompositionRules,
  readValidationOverrides,
  VALIDATION_OVERRIDES_KEY,
  withValidationOverride,
  type BlockRegistry,
  type CompositionForm,
  type RuleIssue,
} from '@hyranai/regta-document-ir';
import { createRegtaBlockRegistry, getComposition } from '@hyranai/regta-blocks';
import {
  effectiveSigningMode,
  evaluateVisibilityRule,
  getTemplateVersion,
  type AttachmentCreateSeed,
  type AttachmentRef,
  type TemplateModule,
} from '@hyranai/regta-templates';
import {
  ApiError,
  tenantApi,
  type ContractDetail,
  type ContractParty,
  type ContractReview,
  type PartySnapshotInput,
} from '../../api.js';
import { useTenantSession } from '../../auth-context.js';
import { useInvalidate, useTenantQuery } from '../../query.js';
import { usePageTitle } from '../../use-page-title.js';
import { FrozenPdfPreview, StatusBar, type SaveState } from '../../components/ContractStatusBar.js';
import { ReviewPanel, ClosedReviewBanner } from '../../components/ReviewPanel.js';
import { SigningPanel } from '../../components/SigningPanel.js';
import { SigningPlanPanel, useSigningDraft } from '../../components/SigningPlanPanel.js';
import { SIGNING_PHASE_STATUSES, signingTabState } from '../../lib/signing-tab.js';
import { PartiesPanel, PartySlotCard, type LocalParty } from '../../components/PartiesPanel.js';
import { CopyFromContractModal } from '../../components/CopyFromContractModal.js';
import { PropertyAutofillGroup } from '../../components/autofill/PropertyAutofillGroup.js';
import { MultiPropertyPanel } from '../../components/MultiPropertyPanel.js';
import { BilagorTab } from '../../components/BilagorTab.js';
import { CreateBilagaDialog } from '../../components/CreateBilagaDialog.js';
import { NotificationsTab } from '../../components/NotificationsTab.js';
import { UnsavedChangesGuard } from '../../components/UnsavedChangesGuard.js';
import { useDataTableAutoImport } from '../../components/DataTableAutoImport.js';
import { adaptWindow, adaptationCount, changesLabel } from './finetune/model.js';
import { STALE_APPROVAL_NOTICE, approvalIsStale, useApprovalResetGuard } from '../../lib/approval-reset.js';
import type { AutofillMode } from '../../components/autofill/AutofillModeField.js';
import { groupReminders } from '../../lib/reminder-groups.js';
import { irErrorText } from '../../lib/ir-errors.js';
import { partiesUpdatePayload, previewPartyOverlay } from '../../lib/party-draft.js';
import {
  isShown,
  nextEmpty,
  partitionDiagnostics,
  requiredProgress,
  shownGaps,
  shownRuleIssues,
  useFormTouched,
} from '../../lib/form-touched.js';
import {
  locateAnchor,
  scrollToLocateAnchor,
  type LocateKind,
} from '../../lib/locate-anchor.js';
import { BetaFormGroups } from './BetaFormGroups.js';
import { BetaBilagaEditor, isBetaBilaga } from './BetaBilagaEditor.js';
import { RuleMessage, type RuleActions } from './RuleMessages.js';
import { placePanels } from './panel-placement.js';
import { IrDiagnostics, IrPreviewFrame, useIrPreview } from './IrPreview.js';
import { IrRevisionsPanel } from './IrRevisionsPanel.js';
import { useReviewItems } from '../../components/beta-review/useReviewItems.js';
import { useFieldActions, type FieldTarget } from '../../components/beta-review/FieldActions.js';
import { SigningGateNotice } from '../../components/beta-review/SigningGateNotice.js';
import {
  BILAGA_PARAM,
  FIELD_PARAM,
  attachmentIdOf,
  commentsLabel,
  fieldRequestsLabel,
  previewMarkers,
  reviewHref,
  suggestionsLabel,
  type PreviewMarker,
} from '../../components/beta-review/review-model.js';
import { EDITOR_AVTAL_WIDTH, EDITOR_CHROME_WIDTH } from '../../lib/editor-layout.js';
import {
  customValuesErrorText,
  effectiveValues,
  panelPropertySlot,
  toLegacyValues,
  toPanelPartySlots,
  withCustomFields,
  withLegacyAutofill,
  type EditorField,
} from './form-model.js';

let blockRegistry: BlockRegistry | null = null;
const registry = (): BlockRegistry => (blockRegistry ??= createRegtaBlockRegistry());

const LIVE_STATUSES = new Set<ContractDetail['status']>(['draft', 'pending', 'in_review']);

const IN_REVIEW_LOCK_NOTE =
  'Avtalet är under granskning – uppgifter kan inte ändras. Kommentera ett fält eller gå tillbaka till utkast för att ändra.';
type Tab = 'signering' | 'avtal' | 'bilagor' | 'paminnelser' | 'granskning' | 'versioner';
const TABS: ReadonlyArray<Tab> = ['signering', 'avtal', 'bilagor', 'paminnelser', 'granskning', 'versioner'];

function toPartyInput(p: LocalParty): PartySnapshotInput {
  return {
    role: p.role,
    ordinal: p.ordinal,
    id_type: p.id_type,
    id_number: p.id_number,
    legal_name: p.legal_name,
    display_name: p.display_name,
    email: p.email,
    phone: p.phone,
    address_line1: p.address_line1,
    address_line2: p.address_line2,
    postal_code: p.postal_code,
    city: p.city,
    country: p.country,
    provenance: p.provenance,
  };
}

function fromServerParty(p: ContractParty): LocalParty {
  return {
    _clientId: p.id,
    _persistedId: p.id,
    role: p.role,
    ordinal: p.ordinal,
    id_type: p.idType,
    id_number: p.idNumber,
    legal_name: p.legalName,
    display_name: p.displayName,
    email: p.email,
    phone: p.phone,
    address_line1: p.addressLine1,
    address_line2: p.addressLine2,
    postal_code: p.postalCode,
    city: p.city,
    country: p.country,
    provenance: p.provenance,
    privacy_class: p.privacyClass,
  };
}

export function BetaContractEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { token, actor } = useTenantSession();
  const invalidate = useInvalidate();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  const { run: dataTableAutoImport, element: dataTableAutoImportEl } = useDataTableAutoImport();

  const contractQ = useTenantQuery(
    'contracts.get',
    (cid) => tenantApi.contracts.get(cid as string),
    id ? [id] : [],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const partiesQ = useTenantQuery(
    'contract_parties.list',
    (cid) => tenantApi.contract_parties.list({ contract_id: cid as string }),
    id ? [id] : [],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const reviewsQ = useTenantQuery(
    'reviews.list_for_contract',
    (cid) => tenantApi.reviews.list_for_contract({ contract_id: cid as string }),
    id ? [id] : [],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const notificationsQ = useTenantQuery(
    'notifications.list_by_contract',
    (cid) => tenantApi.notifications.list_by_contract({ contract_id: cid as string }),
    id ? [id] : [],
    { revalidateOnFocus: false },
  );
  const reminderCount = groupReminders(notificationsQ.data?.notifications ?? []).filter(
    (g) => !g.allHandled,
  ).length;

  const reviewsList: ContractReview[] = reviewsQ.data?.reviews ?? [];
  const activeReview = reviewsList.find((r) => r.status === 'pending') ?? null;
  const isActiveReviewer = !!activeReview && !!actor && activeReview.reviewer.id === actor.id;
  const lastClosedReview = reviewsList.find((r) => r.status !== 'pending') ?? null;
  const hasReviewHistory = reviewsList.some((r) => r.status !== 'pending');

  const contract: ContractDetail | null = contractQ.data?.contract ?? null;
  // ADR-0102 decision 3: confirm once before a change resets a current approval.
  const approvalGuard = useApprovalResetGuard(contract?.approval);
  const serverParties: ContractParty[] = useMemo(() => partiesQ.data?.parties ?? [], [partiesQ.data]);

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());
  const [localParties, setLocalParties] = useState<LocalParty[]>([]);
  const [removedPartyIds, setRemovedPartyIds] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [missing, setMissing] = useState<{
    fields: string[];
    slots: string[];
    attachmentSlots: string[];
  } | null>(null);
  const [tab, setTab] = useState<Tab>(
    TABS.includes(initialTab as Tab) ? (initialTab as Tab) : 'avtal',
  );
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [proceeding, setProceeding] = useState(false);
  const [modeBusyKey, setModeBusyKey] = useState<string | null>(null);
  const [focus, setFocus] = useState<{ instanceId: string; seq: number } | null>(null);
  const [bilagaLocate, setBilagaLocate] = useState<{ slotId: string; seq: number } | null>(null);
  useEffect(() => {
    if (tab !== 'bilagor') setBilagaLocate(null);
  }, [tab]);

  const [createSeed, setCreateSeed] = useState<AttachmentCreateSeed | null>(null);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const createResolverRef = useRef<((id: string | null) => void) | null>(null);
  const openCreateAttachment = useCallback(
    (seed: AttachmentCreateSeed): Promise<string | null> =>
      new Promise<string | null>((resolve) => {
        createResolverRef.current = resolve;
        setCreateSeed(seed);
      }),
    [],
  );
  const finishCreateAttachment = useCallback((createdId: string | null) => {
    setCreateSeed(null);
    const resolve = createResolverRef.current;
    createResolverRef.current = null;
    resolve?.(createdId);
  }, []);

  const tmpl: TemplateModule | null = useMemo(
    () => (contract ? getTemplateVersion(contract.templateKey, contract.templateVersion) : null),
    [contract],
  );
  const legacyFields = useMemo(
    () =>
      new Map<string, EditorField>(
        ((tmpl?.variableSchema ?? []) as ReadonlyArray<EditorField>).map((f) => [f.key, f]),
      ),
    [tmpl],
  );

  useEffect(() => {
    if (!contract) return;
    setValues(contract.values ?? {});
    setDirtyKeys(new Set());
    setMissing(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract?.id]);

  const valuesSignature = useMemo(() => JSON.stringify(contract?.values ?? {}), [contract?.values]);
  useEffect(() => {
    if (!contract || dirtyKeys.size > 0) return;
    setValues(contract.values ?? {});
    setMissing(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesSignature]);

  const serverPartiesSignature = useMemo(
    () => serverParties.map((p) => p.id).sort().join(','),
    [serverParties],
  );
  useEffect(() => {
    if (!contract) return;
    setLocalParties(serverParties.map(fromServerParty));
    setRemovedPartyIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract?.id, serverPartiesSignature]);

  useEffect(() => {
    const e = contractQ.error ?? partiesQ.error;
    if (e) setErr((e as Error).message);
  }, [contractQ.error, partiesQ.error]);

  useEffect(() => {
    if (!contract) return;
    setProceeding(false);
    if (initialTab) return;
    setTab(SIGNING_PHASE_STATUSES.has(contract.status) ? 'signering' : 'avtal');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract?.id]);

  const live = !!contract && LIVE_STATUSES.has(contract.status);
  // ADR-0100: a saved signing plan changes the preview (planned names, Ort/Datum).
  const signingPlanQ = useSigningDraft(contract?.id ?? null);
  const refreshKey = contract
    ? [
        contract.updatedAt,
        signingPlanQ.data?.updated_at ?? '',
        contract.status,
        serverPartiesSignature,
        contract.linkedProperties.map((p) => p.id).join(','),
        (contract.attachments ?? []).map((a) => a.id).join(','),
      ].join('|')
    : '';
  // Unsaved party edits ride along so the preview updates before save.
  const unsavedParties = useMemo(
    () => previewPartyOverlay(localParties, removedPartyIds),
    [localParties, removedPartyIds],
  );
  const preview = useIrPreview(
    contract?.documentEngine === 'ir' ? contract.id : null,
    values,
    live,
    refreshKey,
    unsavedParties,
  );
  const autoValues = useMemo(
    () => preview.data?.autofill?.autoValues ?? {},
    [preview.data],
  );

  // Own fields (ADR-0102 addendum) are defined in the action log; their
  // values save with the rest of the form under "Egna uppgifter".
  const actionLogQ = useTenantQuery(
    'contracts.finetune_get',
    (cid) => tenantApi.contracts.finetune_get({ contractId: cid as string }),
    contract?.documentEngine === 'ir' ? [contract.id, contract.status] : [],
    { ignoreActiveGroup: true, revalidateOnFocus: false, enabled: contract?.documentEngine === 'ir' },
  );
  const actionLog = actionLogQ.data?.log ?? null;
  const form: CompositionForm | null = useMemo(() => {
    if (!contract) return null;
    const composition = getComposition(
      contract.templateKey,
      contract.templateVersion,
      effectiveValues(null, values, autoValues),
    );
    return composition
      ? withCustomFields(withLegacyAutofill(describeCompositionForm(composition, registry()), legacyFields), actionLog)
      : null;
  }, [contract, values, autoValues, legacyFields, actionLog]);
  const scope = useMemo(() => effectiveValues(form, values, autoValues), [form, values, autoValues]);
  const legacyValues = useMemo(() => toLegacyValues(legacyFields, values), [legacyFields, values]);
  const formState = useMemo(() => (form ? computeFormState(form, scope) : null), [form, scope]);
  // ADR-0101: block validation rules, live over the effective values.
  const ruleEval = useMemo(() => {
    if (!contract) return null;
    const composition = getComposition(contract.templateKey, contract.templateVersion, scope);
    return composition
      ? evaluateCompositionRules(composition, registry(), createRuleContext({ now: new Date() }), scope)
      : null;
  }, [contract, scope]);

  // Validation display follows touched fields: a new document starts quiet;
  // imported contracts start with every gap revealed.
  const touch = useFormTouched(contract?.id ?? null, !!contract?.originalDocument);
  const missingKeys = useMemo(
    () => (formState?.missing ?? []).map((f) => f.key).filter((k) => !k.startsWith('__')),
    [formState],
  );
  const requiredKeys = useMemo(
    () =>
      (formState?.visible ?? [])
        .filter((f) => (f.required || f.statutory) && !f.key.startsWith('__'))
        .map((f) => f.key),
    [formState],
  );
  const lastJumpRef = useRef<string | null>(null);

  const partiesDirty =
    removedPartyIds.length > 0 || localParties.some((p) => !p._persistedId || p._dirty);
  const dirty = dirtyKeys.size > 0 || partiesDirty;

  const onFieldChange = useCallback(
    (key: string, next: unknown) => {
      if (!approvalGuard()) return;
      setValues((prev) => ({ ...prev, [key]: next }));
      setDirtyKeys((s) => new Set(s).add(key));
      setMissing(null);
    },
    [approvalGuard],
  );

  const onFormFieldChange = useCallback(
    (key: string, next: unknown) => {
      touch.edited(key, isShown(touch.state, key) && missingKeys.includes(key));
      onFieldChange(key, next);
    },
    [touch, missingKeys, onFieldChange],
  );

  const applyRuleFix = useCallback(
    (issue: RuleIssue) => {
      if (!approvalGuard()) return;
      for (const [key, next] of Object.entries(issue.fix?.set ?? {})) onFieldChange(key, next);
    },
    [onFieldChange, approvalGuard],
  );
  // "Behåll ändå": snapshot the values the rule read; it lapses when they change.
  const overrideRule = useCallback(
    (issue: RuleIssue, keep: boolean) => {
      if (!ruleEval) return;
      onFieldChange(VALIDATION_OVERRIDES_KEY, withValidationOverride(values, ruleEval, issue, keep));
    },
    [ruleEval, values, onFieldChange],
  );
  // A lapsed override is gone for good — the server prunes it on the next save too.
  useEffect(() => {
    if (!ruleEval || ruleEval.lapsed.length === 0) return;
    const lapsed = new Set(ruleEval.lapsed);
    setValues((prev) => {
      const list = readValidationOverrides(prev);
      const kept = list.filter((o) => !lapsed.has(o.id));
      return kept.length === list.length ? prev : { ...prev, [VALIDATION_OVERRIDES_KEY]: kept };
    });
  }, [ruleEval]);

  const onAddParty = useCallback((party: LocalParty) => {
    if (!approvalGuard()) return;
    setLocalParties((prev) => {
      const occupant = prev.find((p) => p.role === party.role && p.ordinal === party.ordinal);
      let next = prev;
      if (occupant) {
        if (occupant._persistedId) {
          setRemovedPartyIds((ids) =>
            ids.includes(occupant._persistedId!) ? ids : [...ids, occupant._persistedId!],
          );
        }
        next = prev.filter((p) => p._clientId !== occupant._clientId);
      }
      return [...next, party];
    });
    setMissing(null);
  }, [approvalGuard]);

  const onRemoveParty = useCallback((clientId: string) => {
    if (!approvalGuard()) return;
    setLocalParties((prev) => {
      const target = prev.find((p) => p._clientId === clientId);
      if (!target) return prev;
      if (target._persistedId) {
        setRemovedPartyIds((ids) =>
          ids.includes(target._persistedId!) ? ids : [...ids, target._persistedId!],
        );
      }
      return prev.filter((p) => p._clientId !== clientId);
    });
    setMissing(null);
  }, [approvalGuard]);

  // "Redigera": auto-mode fields follow via the preview's autofill, so only
  // the party list changes here.
  const onUpdateParty = useCallback((clientId: string, next: LocalParty) => {
    if (!approvalGuard()) return;
    setLocalParties((prev) => prev.map((p) => (p._clientId === clientId ? next : p)));
    setMissing(null);
  }, [approvalGuard]);

  const save = useCallback(async () => {
    if (!token || !contract) return;
    setSaveState('saving');
    setErr(null);
    try {
      const valuesPayload: Record<string, unknown> = {};
      for (const k of dirtyKeys) valuesPayload[k] = values[k];
      const partiesAdd = localParties.filter((p) => !p._persistedId).map(toPartyInput);
      const partiesUpdate = partiesUpdatePayload(localParties);
      await tenantApi.contracts.save_draft({
        id: contract.id,
        values: Object.keys(valuesPayload).length > 0 ? valuesPayload : undefined,
        parties_add: partiesAdd.length > 0 ? partiesAdd : undefined,
        parties_update: partiesUpdate.length > 0 ? partiesUpdate : undefined,
        parties_remove: removedPartyIds.length > 0 ? removedPartyIds : undefined,
      });
      setDirtyKeys(new Set());
      setRemovedPartyIds([]);
      if (partiesUpdate.length > 0) {
        setLocalParties((prev) => prev.map((p) => (p._dirty ? { ...p, _dirty: false } : p)));
      }
      await Promise.all([
        invalidate('contracts.get', contract.id),
        invalidate('contract_parties.list', contract.id),
      ]);
      setSaveState('idle');
    } catch (e) {
      setErr(
        e instanceof ApiError && e.message === 'custom_values_invalid'
          ? customValuesErrorText(e.data, (key) => form?.fields.find((f) => f.key === key)?.label ?? null)
          : (e as Error).message,
      );
      setSaveState('error');
    }
  }, [token, contract, dirtyKeys, values, localParties, removedPartyIds, invalidate, form]);

  const switchAutofillMode = useCallback(
    async (key: string, mode: AutofillMode) => {
      if (!contract || !approvalGuard()) return;
      setModeBusyKey(key);
      setErr(null);
      try {
        const r = await tenantApi.contracts.set_autofill_mode({
          contractId: contract.id,
          fieldKey: key,
          mode,
        });
        setValues((prev) => ({ ...prev, ...r.values }));
        setDirtyKeys((prev) => {
          const n = new Set(prev);
          for (const k of Object.keys(r.values)) n.delete(k);
          return n;
        });
        await invalidate('contracts.get', contract.id);
      } catch (e) {
        setErr(irErrorText((e as Error).message, 'Kunde inte byta läge'));
      } finally {
        setModeBusyKey(null);
      }
    },
    [contract, invalidate, approvalGuard],
  );

  const promote = useCallback(async () => {
    if (!token || !contract) return;
    setErr(null);
    touch.revealAll();
    try {
      const r = await tenantApi.contracts.promote_to_pending({ id: contract.id });
      if (r.ok) {
        setMissing(null);
        await invalidate('contracts.get', contract.id);
      } else {
        setMissing({
          fields: r.missing_fields,
          slots: r.missing_slots,
          attachmentSlots: r.missing_attachment_slots,
        });
        const first = missingKeys[0] ?? r.missing_fields[0] ?? r.validation_errors?.[0]?.slot_key;
        if (first) {
          setTab('avtal');
          setMobilePreviewOpen(false);
          scrollToLocateAnchor([locateAnchor('field', first)]);
        } else if (r.missing_slots[0]) {
          setTab('avtal');
          setMobilePreviewOpen(false);
          scrollToLocateAnchor([locateAnchor('slot', r.missing_slots[0])]);
        }
      }
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token, contract, invalidate, touch, missingKeys]);

  const revertToDraft = useCallback(async () => {
    if (!token || !contract) return;
    // A cancelled signing attempt reopens via the signing seam
    // (supersedes the issued document, clears the attempt).
    const leavingSigning = contract.status === 'cancelled';
    if (
      leavingSigning &&
      !confirm(
        'Återgå till utkast? Avtalet blir redigerbart igen och ett nytt dokument skapas vid nästa utskick. Ändras avtalet bör det granskas på nytt innan det skickas.',
      )
    ) {
      return;
    }
    setErr(null);
    try {
      if (leavingSigning) await tenantApi.signing.return_to_draft({ contract_id: contract.id });
      else await tenantApi.contracts.revert_to_draft({ id: contract.id });
      await invalidate('contracts.get', contract.id);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token, contract, invalidate]);

  const archive = useCallback(async () => {
    if (!token || !contract) return;
    if (!confirm(`Arkivera "${contract.title}"?`)) return;
    setErr(null);
    try {
      await tenantApi.contracts.archive({ id: contract.id });
      await invalidate('contracts.list');
      navigate('/contracts');
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token, contract, invalidate, navigate]);

  const cancelActiveReview = useCallback(async () => {
    if (!token || !contract || !activeReview) return;
    setErr(null);
    try {
      await tenantApi.reviews.cancel({ review_id: activeReview.id });
      await Promise.all([
        invalidate('reviews.list_for_contract', contract.id),
        invalidate('contracts.get', contract.id),
        invalidate('contracts.list'),
        invalidate('reviews.list_my_pending'),
      ]);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token, contract, activeReview, invalidate]);

  const requestReview = useCallback(
    async (reviewerId: string, comment: string | undefined) => {
      if (!token || !contract) return;
      setErr(null);
      touch.revealAll();
      try {
        await tenantApi.reviews.request({
          contract_id: contract.id,
          reviewer_user_id: reviewerId,
          comment,
        });
        await Promise.all([
          invalidate('reviews.list_for_contract', contract.id),
          invalidate('contracts.get', contract.id),
          invalidate('contracts.list'),
        ]);
      } catch (e) {
        setErr((e as Error).message);
        throw e;
      }
    },
    [token, contract, invalidate, touch],
  );

  const mergeServerValues = useCallback(
    (serverValues: Record<string, unknown>) => {
      setValues((prev) => {
        const next: Record<string, unknown> = { ...serverValues };
        for (const key of dirtyKeys) next[key] = prev[key];
        return next;
      });
    },
    [dirtyKeys],
  );
  const linkProperty = useCallback(
    async (propertyId: string) => {
      if (!contract || !approvalGuard()) return;
      const r = await tenantApi.contracts.link_property({ id: contract.id, property_id: propertyId });
      mergeServerValues(r.values);
      await invalidate('contracts.get', contract.id);
    },
    [contract, invalidate, mergeServerValues, approvalGuard],
  );
  const unlinkProperty = useCallback(async () => {
    if (!contract || !approvalGuard()) return;
    const r = await tenantApi.contracts.unlink_property({ id: contract.id });
    mergeServerValues(r.values);
    await invalidate('contracts.get', contract.id);
  }, [contract, invalidate, mergeServerValues, approvalGuard]);

  // Beta contracts: form values are locked for everyone in review (ADR-0102 addendum 4).
  const canEdit = !!contract && contract.status === 'draft';

  const baseTitle = contract ? contract.title || 'Avtal' : null;
  usePageTitle(baseTitle === null ? null : dirty && canEdit ? `● ${baseTitle}` : baseTitle);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S'))) return;
      e.preventDefault();
      if (dirty && canEdit && saveState !== 'saving') void save();
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [dirty, canEdit, saveState, save]);

  const focusInstance = useCallback((instanceId: string) => {
    setFocus((cur) =>
      cur?.instanceId === instanceId ? cur : { instanceId, seq: (cur?.seq ?? 0) + 1 },
    );
  }, []);

  // Review items across every document (ADR-0102 addenda 2 / 3): margin markers, signing gate, field navigation.
  const irBilagaIds = useMemo(
    () => (contract?.attachments ?? []).filter(isBetaBilaga).map((a) => a.id),
    [contract?.attachments],
  );
  const reviewItems = useReviewItems(
    contract?.documentEngine === 'ir' ? contract.id : null,
    irBilagaIds,
    contract ? `${contract.updatedAt}|${contract.status}` : '',
  );
  const markers = useMemo(
    () => previewMarkers(reviewItems.threads, reviewItems.suggestions, reviewItems.changeAddresses),
    [reviewItems.threads, reviewItems.suggestions, reviewItems.changeAddresses],
  );
  const [bilagaFocus, setBilagaFocus] = useState<{ id: string; seq: number } | null>(null);
  const fieldAnchorsRef = useRef<(key: string) => string[]>((key) => [locateAnchor('field', key)]);
  const fieldLabelRef = useRef<(key: string) => string | null>(() => null);
  const openField = useCallback((t: FieldTarget) => {
    setMobilePreviewOpen(false);
    if (t.attachmentId) {
      setTab('bilagor');
      setBilagaFocus((cur) => ({ id: t.attachmentId!, seq: (cur?.seq ?? 0) + 1 }));
      scrollToLocateAnchor([locateAnchor('field', t.key)]);
    } else {
      setTab('avtal');
      scrollToLocateAnchor(fieldAnchorsRef.current(t.key));
    }
  }, []);
  const fieldActions = useFieldActions({
    contract,
    fieldLabel: (t) => (t.attachmentId ? null : fieldLabelRef.current(t.key)),
    onOpenField: openField,
    onCommented: reviewItems.refresh,
  });
  // "Gå till fältet" from Anpassa text / Granskning lands here with ?falt=<key>[&bilaga=<id>].
  const fieldParamDone = useRef(false);
  useEffect(() => {
    if (!contract || fieldParamDone.current) return;
    const key = searchParams.get(FIELD_PARAM);
    if (!key) return;
    fieldParamDone.current = true;
    openField({ key, attachmentId: searchParams.get(BILAGA_PARAM) });
  }, [contract, searchParams, openField]);

  if (contractQ.error instanceof ApiError && contractQ.error.status === 404) {
    return (
      <div className="rounded border border-stone-200 bg-white p-4 text-sm text-stone-600">
        Avtalet finns inte längre.{' '}
        <Link to="/contracts" className="font-medium text-stone-900 underline">
          Till avtalslistan
        </Link>
      </div>
    );
  }
  if (contract && initialTab === 'redigera') {
    return <Navigate to={`/contracts/${contract.id}/beta/anpassa`} replace />;
  }
  if (contract && contract.documentEngine !== 'ir') {
    const qs = searchParams.toString();
    return <Navigate to={`/contracts/${contract.id}/edit${qs ? `?${qs}` : ''}`} replace />;
  }
  if (err && !contract) {
    return (
      <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">{err}</div>
    );
  }
  if (!contract || !tmpl || !token) {
    return <div className="text-sm text-stone-400">loading…</div>;
  }

  const disabled = !canEdit;
  const effectiveSigning = effectiveSigningMode(tmpl.metadata, values);
  const attachments = (contract.attachments ?? []) as ReadonlyArray<AttachmentRef>;

  const partySlots = form && form.partySlots.length > 0
    ? toPanelPartySlots(form.partySlots)
    : tmpl.metadata.partySlots.filter((s) => evaluateVisibilityRule(s.visibleWhen, legacyValues));
  const propertySlot = panelPropertySlot(
    form?.propertySlots ?? [],
    tmpl.metadata.propertySlots,
    (form?.fields ?? []).some((f) => f.autofill?.kind === 'property' || f.autofill?.kind === 'property_list'),
  );

  // Party and property pickers sit next to the fields they fill; slots with
  // no visible bound field stay in the compact panel at the top.
  const placement =
    form && formState ? placePanels(form, formState, partySlots, propertySlot !== null) : null;
  const slotKey = (s: { role: string; ordinal: number }) => `${s.role}#${s.ordinal}`;
  const topSlotKeys = new Set(
    (placement ? placement.topSlots.map((i) => partySlots[i]!) : partySlots).map(slotKey),
  );
  const hasOffSlotParties = localParties.some(
    (p) => !partySlots.some((s) => s.role === p.role && s.ordinal === p.ordinal),
  );
  const showTopParties = topSlotKeys.size > 0 || hasOffSlotParties;
  const propertyPanel: ReactNode = propertySlot?.multi ? (
    <MultiPropertyPanel
      token={token}
      contractId={contract.id}
      linkedProperties={contract.linkedProperties}
      defaultGroupId={contract.groupId}
      required={propertySlot.required}
      label={propertySlot.label}
      help={propertySlot.help}
      onChanged={async () => {
        await invalidate('contracts.get', contract.id);
      }}
      disabled={disabled}
    />
  ) : propertySlot ? (
    <PropertyAutofillGroup
      token={token}
      fields={[]}
      values={values}
      linkedId={contract.propertyId}
      defaultGroupId={contract.groupId}
      onLink={linkProperty}
      onUnlink={unlinkProperty}
      onFieldChange={onFieldChange}
      disabled={disabled}
    />
  ) : null;
  const beforeField = (key: string): ReactNode => {
    const cards = (placement?.partiesBefore.get(key) ?? []).map((i) => {
      const slot = partySlots[i]!;
      return (
        <PartySlotCard
          key={slotKey(slot)}
          token={token}
          slot={slot}
          party={localParties.find((p) => p.role === slot.role && p.ordinal === slot.ordinal) ?? null}
          fields={[]}
          values={values}
          onAddParty={onAddParty}
          onRemoveParty={onRemoveParty}
          onUpdateParty={onUpdateParty}
          onFieldChange={onFieldChange}
          disabled={disabled}
        />
      );
    });
    const property = placement?.propertyBefore === key ? propertyPanel : null;
    return cards.length > 0 || property ? (
      <>
        {property}
        {cards}
      </>
    ) : null;
  };

  const fieldLabel = (key: string): string | null =>
    form?.fields.find((f) => f.key === key)?.label ?? null;
  const locateField = (key: string) => {
    setTab('avtal');
    setMobilePreviewOpen(false);
    scrollToLocateAnchor([locateAnchor('field', key)]);
  };
  const fieldAnchors = (key: string): string[] => {
    const anchors = [locateAnchor('field', key)];
    const af = form?.fields.find((f) => f.key === key)?.autofill;
    if (af?.kind === 'party') {
      const slot = partySlots.find((sl) => sl.role === af.role && sl.ordinal === (af.ordinal ?? 1));
      if (slot) anchors.push(locateAnchor('slot', slot.id));
    }
    return anchors;
  };
  fieldAnchorsRef.current = fieldAnchors;
  fieldLabelRef.current = fieldLabel;
  const jumpToGap = (key: string) => {
    lastJumpRef.current = key;
    setTab('avtal');
    setMobilePreviewOpen(false);
    scrollToLocateAnchor(fieldAnchors(key));
  };
  const jumpToNextEmpty = () => {
    const key = nextEmpty(missingKeys, lastJumpRef.current);
    if (key) jumpToGap(key);
  };
  const progress = requiredProgress(requiredKeys, missingKeys);
  const revealedGaps = touch.state.revealAll ? shownGaps(touch.state, missingKeys) : [];
  const shownRules = ruleEval ? shownRuleIssues(touch.state, ruleEval.issues) : [];
  const blockingRules = shownRules.filter((i) => i.severity === 'error' && !i.overridden);
  const ruleActions: RuleActions = { onApplyFix: applyRuleFix, onOverride: overrideRule, disabled };
  const statutoryGapKeys = new Set((formState?.missingStatutory ?? []).map((f) => f.key));
  const proceedToSigning = () => {
    touch.revealAll();
    const blocking = missingKeys.find((k) => statutoryGapKeys.has(k)) ?? ruleEval?.blocking[0]?.anchorKey;
    if (blocking) {
      jumpToGap(blocking);
      return;
    }
    setProceeding(true);
    setTab('signering');
  };

  const locateMissing = (kind: LocateKind, targetId: string, anchors: ReadonlyArray<string>) => {
    if (kind === 'attachment_slot') {
      setTab('bilagor');
      setBilagaLocate((cur) => ({ slotId: targetId, seq: (cur?.seq ?? 0) + 1 }));
    } else {
      setTab('avtal');
      setMobilePreviewOpen(false);
    }
    scrollToLocateAnchor(anchors);
  };

  // ADR-0100: before signing starts (draft, pending, in_review) the tab shows
  // the signing plan, so author and reviewer see the planned signers.
  const signingTab = signingTabState(contract.status, proceeding);
  const signingTabAvailable = signingTab.available;
  const planPhase = signingTab.panel === 'plan';
  const effectiveTab: Tab =
    (tab === 'signering' && !signingTabAvailable) || (tab === 'granskning' && !hasReviewHistory)
      ? 'avtal'
      : tab;
  const diagnostics = preview.data?.diagnostics ?? [];
  // "Anpassa text" (ADR-0102): the action log replayed over the preview compile.
  const adaptation = preview.data?.finetune ?? null;
  const adaptedCount = adaptationCount(adaptation?.groups);
  const adaptConflicts = adaptation?.conflicts.length ?? 0;
  const adaptAllowed = adaptWindow(contract.status).allowEdit;
  const adaptHref = `/contracts/${contract.id}/beta/anpassa`;
  const reviewOpenCount = reviewItems.openSuggestions + reviewItems.openThreads;
  const reviewSummary = [
    reviewItems.openSuggestions > 0 ? suggestionsLabel(reviewItems.openSuggestions) : null,
    reviewItems.openFieldRequests > 0 ? fieldRequestsLabel(reviewItems.openFieldRequests) : null,
    reviewItems.openThreads - reviewItems.openFieldRequests > 0
      ? commentsLabel(reviewItems.openThreads - reviewItems.openFieldRequests)
      : null,
  ]
    .filter(Boolean)
    .join(', ');
  const gateBlocked =
    reviewItems.loaded &&
    (reviewItems.openSuggestions > 0 || reviewItems.openFieldRequests > 0) &&
    (contract.status === 'draft' || contract.status === 'pending');
  const gateItems = {
    openSuggestions: reviewItems.openSuggestions,
    openFieldRequests: reviewItems.openFieldRequests,
    byDocument: [...reviewItems.suggestions.entries()].map(([key, list]) => ({
      attachmentId: attachmentIdOf(key),
      count: list.filter((x) => x.open).length,
    })),
  };
  const documentTitle = (attachmentId: string | null): string => {
    if (!attachmentId) return 'Huvudavtal';
    const a = attachments.find((x) => x.id === attachmentId);
    return a ? `${a.number != null ? `Bilaga ${a.number}` : 'Bilaga'} – ${a.displayName}` : 'Bilaga';
  };
  const onMarkerClick = (m: PreviewMarker) =>
    navigate(
      reviewHref(
        contract.id,
        m.target.kind === 'field' && m.suggestions === 0 ? 'uppgifter' : 'text',
        attachmentIdOf(m.documentKey),
      ),
    );
  const reviewNote =
    reviewOpenCount > 0 || contract.status === 'in_review' ? (
      <Link
        to={reviewHref(contract.id, reviewItems.openSuggestions > 0 ? 'text' : 'uppgifter')}
        className="inline-flex items-center gap-1 text-[11px] text-amber-900 underline decoration-amber-300 underline-offset-2 hover:decoration-amber-700"
      >
        {reviewOpenCount > 0 ? `Granskning: ${reviewSummary}` : 'Öppna granskningen'}
      </Link>
    ) : null;
  const approval = contract.approval;
  const deviationNeedsReview =
    !!approval?.requireReviewForDeviatingDocuments && !approval.approved && !!adaptation?.deviatesFromStandard;
  const adaptedNote =
    adaptedCount > 0 ? (
      <Link
        to={adaptHref}
        className="inline-flex items-center gap-1 text-[11px] text-violet-800 underline decoration-violet-300 underline-offset-2 hover:decoration-violet-700"
      >
        Texten är anpassad – {changesLabel(adaptedCount)}
        {adaptConflicts > 0 && <span className="text-red-700">({adaptConflicts} att lösa)</span>}
      </Link>
    ) : null;
  const shownDiagnostics = partitionDiagnostics(diagnostics, touch.state);
  const diagnosticErrors = [...shownDiagnostics.value, ...shownDiagnostics.structural].filter(
    (d) => d.severity === 'error',
  ).length;

  const tabButton = (key: Tab, label: ReactNode) => (
    <button
      key={key}
      type="button"
      onClick={() => setTab(key)}
      className={
        'border-b-2 px-3 py-1.5 text-xs font-medium tracking-wider uppercase transition-colors ' +
        (effectiveTab === key
          ? 'border-stone-900 text-stone-900'
          : 'border-transparent text-stone-500 hover:text-stone-700')
      }
    >
      {label}
    </button>
  );

  return (
    <div className="flex h-full flex-col">
      {dataTableAutoImportEl}
      <UnsavedChangesGuard when={dirty && canEdit} />
      <div className={EDITOR_CHROME_WIDTH + ' flex flex-col'}>
      <Link
        to="/contracts"
        className="mb-3 inline-flex w-fit items-center gap-1 text-xs text-stone-500 transition-colors hover:text-stone-800 hover:underline"
      >
        ← Avtal
      </Link>
      <StatusBar
        token={token}
        contract={contract}
        dirty={dirty}
        saveState={saveState}
        onSave={save}
        onPromote={promote}
        onRevertToDraft={revertToDraft}
        onArchive={archive}
        onRequestReview={requestReview}
        onCancelReview={activeReview ? cancelActiveReview : null}
        isActiveReviewer={isActiveReviewer}
        activeReviewerName={
          activeReview ? (activeReview.reviewer.name ?? activeReview.reviewer.email ?? null) : null
        }
        lastClosedReviewStatus={
          lastClosedReview
            ? (lastClosedReview.status as 'approved' | 'rejected' | 'cancelled')
            : null
        }
        missing={missing}
        tmpl={tmpl}
        editable={!disabled}
        onProceedToSigning={proceedToSigning}
        onLocateMissing={locateMissing}
      />

      {actor && activeReview && (
        <div className="mb-4">
          <ReviewPanel token={token} contract={contract} actorId={actor.id} />
        </div>
      )}
      {fieldActions.element}
      {fieldActions.error && (
        <div role="alert" className="mb-4 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800">
          {fieldActions.error}{' '}
          <button type="button" className="underline" onClick={fieldActions.clearError}>
            Stäng
          </button>
        </div>
      )}
      {reviewNote && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-900">
          <span className="flex-1">
            {contract.status === 'in_review'
              ? 'Avtalet granskas. Förslag på text och begäranden om uppgifter samlas på granskningssidan.'
              : 'Det finns öppna punkter från granskningen. De måste vara beslutade eller stängda innan avtalet kan skickas för signering.'}
          </span>
          {reviewNote}
        </div>
      )}
      {approvalIsStale(reviewsList) && (contract.status === 'draft' || contract.status === 'pending') && (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
          {STALE_APPROVAL_NOTICE}
        </div>
      )}
      {!activeReview &&
        lastClosedReview?.status === 'rejected' &&
        (contract.status === 'draft' || contract.status === 'pending') && (
          <div className="mb-4">
            <ClosedReviewBanner review={lastClosedReview} variant="rejected" />
          </div>
        )}

      <div className="mb-3 flex items-center gap-1 overflow-x-auto border-b border-stone-200">
        {signingTabAvailable && tabButton('signering', 'Signering')}
        {tabButton('avtal', 'Avtal')}
        {tabButton(
          'bilagor',
          <>
            Bilagor <span className="font-normal text-stone-400">({attachments.length})</span>
          </>,
        )}
        {tabButton(
          'paminnelser',
          <>
            Påminnelser <span className="font-normal text-stone-400">({reminderCount})</span>
          </>,
        )}
        {hasReviewHistory && tabButton('granskning', 'Granskning')}
        {tabButton('versioner', 'Versioner')}
      </div>
      </div>

      {/* Only the Avtal tab (form + A4 preview) widens on large monitors;
          the header above and the other tabs keep the chrome width. */}
      <div
        className={
          (effectiveTab === 'avtal' ? EDITOR_AVTAL_WIDTH : EDITOR_CHROME_WIDTH) +
          ' flex flex-1 flex-col'
        }
      >
      {effectiveTab === 'signering' ? (
        <main className="flex flex-1 flex-col overflow-auto bg-white">
          <div className="mx-auto flex w-full max-w-2xl flex-col gap-3 p-6">
            {diagnosticErrors > 0 && (
              <div className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-800">
                Dokumentet har {diagnosticErrors} fel som måste åtgärdas innan det kan skickas.{' '}
                <button type="button" className="underline" onClick={() => setTab('avtal')}>
                  Visa diagnostik
                </button>
              </div>
            )}
            {deviationNeedsReview && (
              <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                Texten i avtalet är anpassad och måste granskas och godkännas innan avtalet kan skickas för
                signering.{' '}
                <Link to={adaptHref} className="underline">
                  Visa anpassningarna
                </Link>
              </div>
            )}
            {!planPhase && gateBlocked && (
              <SigningGateNotice contractId={contract.id} items={gateItems} documentLabel={documentTitle} />
            )}
            {planPhase ? (
              <SigningPlanPanel contractId={contract.id} />
            ) : (
              <fieldset disabled={gateBlocked} className={gateBlocked ? 'opacity-60' : undefined}>
                <SigningPanel token={token} contract={contract} signingMode={effectiveSigning} />
              </fieldset>
            )}
          </div>
        </main>
      ) : effectiveTab === 'paminnelser' ? (
        <main className="flex flex-1 flex-col overflow-auto bg-white">
          <NotificationsTab contractId={contract.id} />
        </main>
      ) : effectiveTab === 'granskning' ? (
        <main className="flex flex-1 flex-col overflow-auto bg-white">
          <div className="mx-auto w-full max-w-2xl p-6">
            <p className="mb-3 text-xs text-stone-600">
              Förslag på text, kommentarer och begäranden om uppgifter för alla dokument finns på{' '}
              <Link to={reviewHref(contract.id)} className="font-medium underline">
                granskningssidan
              </Link>
              .
            </p>
            {actor && (
              <ReviewPanel token={token} contract={contract} actorId={actor.id} historyOpen />
            )}
          </div>
        </main>
      ) : effectiveTab === 'versioner' ? (
        <main className="flex flex-1 flex-col overflow-auto bg-white">
          <IrRevisionsPanel contractId={contract.id} refreshKey={refreshKey} />
        </main>
      ) : effectiveTab === 'bilagor' ? (
        <>
          {contract.status === 'in_review' && (
            <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">{IN_REVIEW_LOCK_NOTE}</div>
          )}
          <BilagorTab
            token={token}
            contractId={contract.id}
            parentTemplateKey={contract.templateKey}
            parentValues={legacyValues}
            attachments={contract.attachments ?? []}
            visibleSlots={(tmpl.metadata.attachmentSlots ?? []).filter((s) =>
              evaluateVisibilityRule(s.visibleWhen, legacyValues),
            )}
            missingSlotIds={missing?.attachmentSlots ?? []}
            locateSlot={bilagaLocate}
            locateAttachment={bilagaFocus}
            onChanged={async () => {
              await invalidate('contracts.get', contract.id);
            }}
            disabled={disabled}
            renderTemplateEditor={(attachment, ctx) =>
              isBetaBilaga(attachment) ? (
                <BetaBilagaEditor
                  contractId={contract.id}
                  attachment={attachment}
                  disabled={ctx.disabled}
                  onChanged={ctx.onChanged}
                />
              ) : null
            }
          />
        </>
      ) : (
        <>
          <div
            role="tablist"
            aria-label="Visa fält eller förhandsvisning"
            className="mb-3 grid w-full grid-cols-2 overflow-hidden rounded-md border border-stone-300 bg-stone-100 text-sm lg:hidden"
          >
            {(
              [
                [false, 'Fält', FileText],
                [true, 'Förhandsvisning', Eye],
              ] as const
            ).map(([isPreview, label, Icon]) => (
              <button
                key={label}
                type="button"
                role="tab"
                aria-selected={mobilePreviewOpen === isPreview}
                onClick={() => setMobilePreviewOpen(isPreview)}
                className={
                  'flex items-center justify-center gap-2 px-3 py-2 font-medium transition-colors ' +
                  (isPreview ? 'border-l border-stone-300 ' : '') +
                  (mobilePreviewOpen === isPreview
                    ? 'bg-white text-stone-900 shadow-[inset_0_-2px_0_0_var(--color-stone-900)]'
                    : 'text-stone-500 hover:bg-stone-200/60 hover:text-stone-800')
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
          <div className="grid flex-1 grid-cols-1 gap-6 lg:grid-cols-[420px_1fr] lg:items-start">
            <aside
              className={'min-w-0 flex-col gap-5 lg:flex ' + (mobilePreviewOpen ? 'hidden' : 'flex')}
            >
              {!disabled && contract.status === 'draft' && (
                <button
                  type="button"
                  onClick={() => setCopyModalOpen(true)}
                  className="group flex items-center gap-3 rounded-md border border-indigo-300 bg-gradient-to-r from-indigo-50 to-white px-3 py-2.5 text-left shadow-sm transition-colors hover:border-indigo-400 hover:from-indigo-100"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white">
                    <Copy className="h-4 w-4" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-semibold text-indigo-900">Kopiera från befintligt avtal</span>
                    <span className="text-[11px] leading-tight text-indigo-700/80">
                      Förifyll motpart, fastighet, bilagor och fält
                    </span>
                  </span>
                </button>
              )}
              {(adaptAllowed || adaptedCount > 0) && (
                <Link
                  to={adaptHref}
                  className="group flex items-center gap-3 rounded-md border border-stone-300 bg-white px-3 py-2.5 text-left shadow-sm transition-colors hover:border-stone-400 hover:bg-stone-50"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-stone-900 text-white">
                    <PenLine className="h-4 w-4" />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="text-sm font-semibold text-stone-900">
                      {adaptAllowed ? 'Anpassa text' : 'Visa anpassad text'}
                    </span>
                    <span className="text-[11px] leading-tight text-stone-500">
                      {adaptAllowed
                        ? 'Ändra, ta bort eller lägg till text i avtalet'
                        : 'Avtalet kan inte ändras i sin nuvarande status'}
                    </span>
                  </span>
                  {adaptedCount > 0 && (
                    <span
                      className={
                        'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ' +
                        (adaptConflicts > 0 ? 'bg-red-100 text-red-800' : 'bg-violet-100 text-violet-800')
                      }
                      title={changesLabel(adaptedCount)}
                    >
                      {adaptedCount}
                    </span>
                  )}
                </Link>
              )}
              {err && (
                <div className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-700">
                  {err}
                </div>
              )}

              {progress.total > 0 && (
                <div
                  aria-live="polite"
                  className="flex items-center justify-between gap-2 rounded border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-700"
                >
                  <span>
                    Obligatoriska uppgifter:{' '}
                    <span className="font-medium text-stone-900">
                      {progress.filled} av {progress.total}
                    </span>{' '}
                    ifyllda
                  </span>
                  {missingKeys.length > 0 && !disabled && (
                    <button
                      type="button"
                      onClick={jumpToNextEmpty}
                      className="shrink-0 font-medium text-stone-900 underline decoration-stone-400 decoration-dotted underline-offset-2 hover:decoration-solid"
                    >
                      Nästa tomma
                    </button>
                  )}
                </div>
              )}

              {revealedGaps.length > 0 && (
                <div role="alert" className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800">
                  <div className="mb-1 font-medium">
                    {revealedGaps.length === 1
                      ? '1 obligatorisk uppgift saknas:'
                      : `${revealedGaps.length} obligatoriska uppgifter saknas:`}
                  </div>
                  <ul className="flex flex-wrap gap-x-2 gap-y-0.5">
                    {revealedGaps.map((key) => (
                      <li key={key}>
                        <button
                          type="button"
                          onClick={() => jumpToGap(key)}
                          className="underline decoration-red-400/60 decoration-dotted underline-offset-2 hover:decoration-solid"
                        >
                          {fieldLabel(key) ?? key}
                        </button>
                        {statutoryGapKeys.has(key) && (
                          <span className="ml-0.5 text-[10px] text-red-600">(lagstadgad)</span>
                        )}
                      </li>
                    ))}
                  </ul>
                  {disabled && contract.status === 'pending' && (
                    <p className="mt-1 text-[11px] text-red-700">
                      Återställ till utkast för att fylla i uppgifterna.
                    </p>
                  )}
                </div>
              )}

              {shownRules.length > 0 && (
                <div
                  aria-live="polite"
                  className={
                    'rounded border p-2 text-xs ' +
                    (blockingRules.length > 0 ? 'border-red-300 bg-red-50' : 'border-amber-200 bg-amber-50/60')
                  }
                >
                  <div className="mb-1 font-medium text-stone-800">
                    {blockingRules.length > 0
                      ? 'Rätta felen eller välj Behåll ändå innan avtalet kan gå vidare:'
                      : 'Att kontrollera:'}
                  </div>
                  <ul className="flex flex-col gap-0.5">
                    {shownRules.map((issue) => (
                      <li key={issue.id}>
                        <RuleMessage
                          issue={issue}
                          variant="summary"
                          actions={ruleActions}
                          onLocate={(i) => jumpToGap(i.anchorKey)}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {showTopParties && (
                <PartiesPanel
                  token={token}
                  contractId={contract.id}
                  parties={localParties}
                  values={values}
                  slots={partySlots}
                  variableSchema={[]}
                  onAddParty={onAddParty}
                  onRemoveParty={onRemoveParty}
                  onUpdateParty={onUpdateParty}
                  onFieldChange={onFieldChange}
                  disabled={disabled}
                  excludeSlot={(s) => !topSlotKeys.has(slotKey(s))}
                />
              )}
              {partiesDirty && (
                <p className={(showTopParties ? '-mt-3 ' : '') + 'text-[11px] text-stone-500'}>
                  Spara för att uppdatera dokumentet med de nya parterna.
                </p>
              )}

              {placement?.propertyBefore ? null : propertyPanel}

              {form && formState ? (
                <BetaFormGroups
                  form={form}
                  state={formState}
                  values={values}
                  scope={scope}
                  autoValues={autoValues}
                  legacyFields={legacyFields}
                  attachments={attachments}
                  disabled={disabled}
                  modeLocked={contract.status !== 'draft'}
                  busyKey={modeBusyKey}
                  onFieldChange={onFormFieldChange}
                  isGapShown={(key) => isShown(touch.state, key)}
                  onFieldBlur={touch.blurred}
                  beforeField={beforeField}
                  ruleIssues={shownRules}
                  ruleActions={ruleActions}
                  onSwitchMode={(key, mode) => void switchAutofillMode(key, mode)}
                  onFocusInstance={focusInstance}
                  onCreateAttachment={openCreateAttachment}
                  dataTableAutoImport={dataTableAutoImport}
                />
              ) : (
                <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                  Formuläret för den här mallen finns inte i webbklienten ännu. Förhandsvisningen
                  visar dokumentet som servern bygger det.
                </div>
              )}

              {disabled && (
                <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                  {contract.status === 'in_review'
                    ? IN_REVIEW_LOCK_NOTE
                    : 'Detta avtal är inte i utkast-läge och kan inte redigeras.'}
                </div>
              )}
            </aside>

            <main
              className={
                'flex-col overflow-hidden bg-stone-100 lg:sticky lg:top-[var(--shell-top,3.5rem)] lg:flex lg:h-[calc(100vh_-_var(--shell-top,3.5rem))] ' +
                (mobilePreviewOpen ? 'flex' : 'hidden')
              }
            >
              {live ? (
                <div className="flex min-h-0 flex-1 flex-col">
                  <div className="flex items-center justify-between gap-2 border-b border-stone-200 bg-white px-4 py-1.5 text-[11px] text-stone-500">
                    <span className="inline-flex items-center gap-2">
                      Förhandsvisning
                    </span>
                    {adaptedNote}
                    {reviewNote}
                    <span aria-live="polite">
                      {preview.loading ? 'Uppdaterar…' : preview.error ? '' : 'Aktuell'}
                    </span>
                  </div>
                  {preview.error && (
                    <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">
                      {preview.error}
                    </div>
                  )}
                  <IrDiagnostics
                    diagnostics={diagnostics}
                    touch={touch.state}
                    fieldLabel={fieldLabel}
                    onLocateField={locateField}
                  />
                  {preview.data ? (
                    <IrPreviewFrame
                      preview={preview.data}
                      focus={focus}
                      onValueClick={(t, rect) => fieldActions.open(t, rect)}
                      markers={markers}
                      onMarkerClick={onMarkerClick}
                    />
                  ) : (
                    <div className="flex flex-1 items-center justify-center text-xs text-stone-500">
                      {preview.error ? '' : 'Bygger dokumentet…'}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex min-h-0 flex-1 flex-col">
                  {(adaptedNote || reviewNote) && (
                    <div className="flex flex-wrap items-center gap-3 border-b border-stone-200 bg-white px-4 py-1.5">
                      {adaptedNote}
                      {reviewNote}
                    </div>
                  )}
                  <FrozenPdfPreview token={token} contractId={contract.id} />
                </div>
              )}
            </main>
          </div>
        </>
      )}
      </div>

      {copyModalOpen && (
        <CopyFromContractModal
          contractId={contract.id}
          onClose={() => setCopyModalOpen(false)}
          onCopied={async () => {
            touch.revealAll();
            await Promise.all([
              invalidate('contracts.get', contract.id),
              invalidate('contract_parties.list', contract.id),
            ]);
          }}
        />
      )}
      {createSeed && (
        <CreateBilagaDialog
          contractId={contract.id}
          parentTemplateKey={contract.templateKey}
          defaultName={createSeed.defaultName}
          recommendedTemplate={createSeed.recommendedTemplate}
          onCreated={async (attachmentId) => {
            await invalidate('contracts.get', contract.id);
            finishCreateAttachment(attachmentId);
          }}
          onClose={() => finishCreateAttachment(null)}
        />
      )}
    </div>
  );
}
